<script setup>
import { ref, onBeforeUnmount } from 'vue'
import IconBase from '../icon/IconBase.vue'
import IconBin from '../icon/IconBin.vue'
import FormButton from '../Atoms/FormButton.vue'

const props = defineProps({
	label: { type: String, default: 'Remove' },
	confirmText: { type: String, default: 'Remove this destination?' },
	// Start directly on the confirm step (skip the initial trigger button).
	// Useful when the toggle itself already implies the action (e.g. a trash icon).
	immediate: { type: Boolean, default: false },
})

const emit = defineEmits(['confirm', 'cancel'])

const confirming = ref(props.immediate)
const rootEl = ref(null)

const onDocClick = (e) => {
	if (rootEl.value && !rootEl.value.contains(e.target)) cancel()
}

const start = () => {
	confirming.value = true
	// Defer so this same click doesn't immediately trigger the outside handler
	requestAnimationFrame(() => document.addEventListener('click', onDocClick))
}

const cancel = () => {
	if (props.immediate) {
		// No trigger button to fall back to — let the parent close the popover.
		emit('cancel')
		return
	}
	confirming.value = false
	document.removeEventListener('click', onDocClick)
}

const onConfirm = () => {
	document.removeEventListener('click', onDocClick)
	confirming.value = false
	emit('confirm')
}

onBeforeUnmount(() => document.removeEventListener('click', onDocClick))
</script>

<template>
	<li ref="rootEl" class="w-52 [&>*]:last:rounded-b-md [&>*]:first:rounded-t-md">
		<FormButton v-if="!confirming" @click.stop="start"
			custom-classes="h-10 md:h-8 !p-3 md:!p-2 hover:bg-surface-3 text-surface-text-1 hover:text-error text-nowrap leading-none text-base md:text-tiny flex items-center w-full transition-colors">
			<icon-base class="mr-2" fill="none">
				<icon-bin />
			</icon-base>
			{{ label }}
		</FormButton>
		<div v-else class="p-2 w-full">
			<p class="text-tiny text-surface-9 mb-1.5 px-0.5 leading-snug">{{ confirmText }}</p>
			<div class="flex items-center gap-1.5">
				<FormButton @click.stop="cancel"
					custom-classes="flex-1 min-w-0 text-[11px] font-medium px-2 py-1 rounded bg-surface-3 text-surface-9 hover:bg-surface-4 transition-colors">
					Cancel
				</FormButton>
				<FormButton @click.stop="onConfirm"
					custom-classes="flex-1 min-w-0 text-[11px] font-medium px-2 py-1 rounded bg-error text-white hover:opacity-90 transition-opacity">
					Remove
				</FormButton>
			</div>
		</div>
	</li>
</template>
